/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import PrefixInterface from '../../../interfaces/billing/PrefixInterface';
import AddButton from '../../../components/Buttons/AddButton';
import RemoveButton from '../../../components/Buttons/RemoveButton';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import { PrefixCollectionInterface } from '../../../interfaces/api/PrefixCollectionInterface';
import { prefixesConstants } from '../../../modules/prefixes/reducers';
import Pager from '../../../components/Adescom/Pager';
import { DiscountEntity } from '../PlatformResellerDiscount/PlatformResellerDiscount';
import { DiscountRateEntity } from '../PlatformResellerDiscount/PlatformResellerRateDiscount';

interface PrefixSelectionProps {
    getSelectedPrefixes: (prefixes: Array<PrefixInterface>) => void;
    entity?: DiscountEntity | DiscountRateEntity;
}

const PrefixSelection = (props: PrefixSelectionProps): React.ReactElement => {
    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const useReducer = false;

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const [editEntity, setEditEntity] = useState<DiscountEntity | DiscountRateEntity | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [prefixes, setPrefixes] = useState<PrefixCollectionInterface | null>(null);
    const [selectedPrefixes, setSelectedPrefixes] = useState<Array<PrefixInterface>>(
        props.entity && Array.isArray(props.entity.destination) ? props.entity.destination : []
    );

    const paginationCallbackFunction = useCallback(
        (uri: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/prefixes');
            }

            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                prefixesConstants.LIST,
                filters,
                orders,
                (data: PrefixCollectionInterface | null) => {
                    if (!useReducer) {
                        setPrefixes(data);
                    }
                },
                { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
            );
        },
        [dispatch, filters, orders, useReducer]
    );

    const selectFraction = (prefix: PrefixInterface): void => {
        const newPrefixes = [...selectedPrefixes, { ...prefix }];
        setSelectedPrefixes(newPrefixes);
        props.getSelectedPrefixes(newPrefixes);
    };

    const unselectFraction = (prefix: PrefixInterface): void => {
        let newArr = [...selectedPrefixes];
        selectedPrefixes.forEach((el: PrefixInterface, indx: number) => {
            if (el.id === prefix.id) {
                newArr.splice(indx, 1);
            }
        });
        setSelectedPrefixes(newArr);
        props.getSelectedPrefixes(newArr);
    };

    const updateOrders = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilters = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    useEffect(() => {
        const uri = FormGeneralHelper.getPath('/prefixes');
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            prefixesConstants.LIST,
            filters,
            orders,
            (data: PrefixCollectionInterface | null) => {
                if (!useReducer) {
                    setPrefixes(data);
                }
            },
            { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
        );
    }, [dispatch, filters, orders, useReducer]);

    useEffect(() => {
        console.log(props.entity);
        if (
            props.entity &&
            props.entity.destination &&
            Array.isArray(props.entity.destination) &&
            (!editEntity || (editEntity && editEntity.name !== props.entity.name))
        ) {
            setEditEntity(props.entity);
            setSelectedPrefixes(props.entity?.destination);
        }
        if (!props.entity || props.entity.destination === null) {
            setSelectedPrefixes([]);
        }
    }, [props.entity, editEntity]);

    return (
        <GridContainer style={{ marginTop: 25 }}>
            <GridItem xs={6}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} align="left" style={{ width: 40 }}>
                                <TableHeaderContentCell textAlign="center" title="clients:c.commands" />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="discount:h.name"
                                    updateOrderList={updateOrders}
                                    updateFilterList={updateFilters}
                                    fieldName="mainname"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>{' '}
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="discount:h.prefix"
                                    updateOrderList={updateOrders}
                                    updateFilterList={updateFilters}
                                    fieldName="prefix"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prefixes &&
                            prefixes['hydra:member'].map((el: PrefixInterface) => {
                                return (
                                    <TableRow key={el.id}>
                                        <TableCell classes={tableCellRoot}>
                                            <AddButton
                                                callback={selectFraction}
                                                callbackParam={el}
                                                disabled={selectedPrefixes.some((sf: PrefixInterface) => {
                                                    return sf.id === el.id;
                                                })}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                                        <TableCell classes={tableCellRoot}>{el.prefix}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell classes={tableCellRoot} colSpan={4}>
                                {prefixes && prefixes['hydra:view'] && <Pager callback={paginationCallbackFunction} views={prefixes['hydra:view']} />}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </GridItem>
            <GridItem xs={6}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} align="left" style={{ width: 40 }}>
                                <TableHeaderContentCell textAlign="center" title="clients:c.commands" />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="discount:h.name"
                                    updateOrderList={updateOrders}
                                    updateFilterList={updateFilters}
                                    fieldName="mainname"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="discount:h.prefix"
                                    updateOrderList={updateOrders}
                                    updateFilterList={updateFilters}
                                    fieldName="prefix"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedPrefixes &&
                            selectedPrefixes.map((el: PrefixInterface) => {
                                return (
                                    <TableRow key={'selected_' + el.id}>
                                        <TableCell classes={tableCellRoot}>
                                            {
                                                <RemoveButton
                                                    callback={unselectFraction}
                                                    callbackParam={el}
                                                    disabled={
                                                        !selectedPrefixes.some((sf: PrefixInterface) => {
                                                            return sf.id === el.id;
                                                        })
                                                    }
                                                />
                                            }
                                        </TableCell>
                                        <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                                        <TableCell classes={tableCellRoot}>{el.prefix}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </GridItem>
        </GridContainer>
    );
};

export default PrefixSelection;
